import { useCallback, useEffect, useState } from 'react';
import { fetchRemoteTreasuryPeriodSummary } from '../../infra/reportsRemoteTreasuryRepo';
import type { BranchTreasuryPeriodSummary } from '../../domain/reportsTreasuryTypes';
import { getReportsDateRange } from '../reportsScreen.utils';
import type { DateRangePreset } from '../reportsScreen.constants';

type Params = {
  currentBranchId: string | null | undefined;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  shouldLoad: boolean;
};

export function useReportsTreasuryPane({
  currentBranchId,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  shouldLoad,
}: Params) {
  const [treasurySummary, setTreasurySummary] = useState<BranchTreasuryPeriodSummary | null>(null);
  const [treasuryLoading, setTreasuryLoading] = useState(false);
  const [treasuryError, setTreasuryError] = useState<string | null>(null);

  const reloadTreasury = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!currentBranchId || !shouldLoad) {
        setTreasurySummary(null);
        setTreasuryError(null);
        return;
      }
      setTreasuryLoading(true);
      setTreasuryError(null);
      const range = getReportsDateRange(
        new Date(),
        dateRangePreset,
        dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null
      );
      const summary = await fetchRemoteTreasuryPeriodSummary(
        currentBranchId,
        range.fromIso,
        range.toIso,
        opts
      );
      if (summary == null) {
        setTreasurySummary(null);
        setTreasuryError('No pudimos cargar la plata del período. Revisá la conexión y reintentá.');
      } else {
        setTreasurySummary(summary);
        setTreasuryError(null);
      }
      setTreasuryLoading(false);
    },
    [currentBranchId, shouldLoad, dateRangePreset, customRangeFrom, customRangeTo]
  );

  useEffect(() => {
    void reloadTreasury();
  }, [reloadTreasury]);

  return {
    treasurySummary,
    treasuryLoading,
    treasuryError,
    reloadTreasury,
  };
}
